import { RANGE_FIELD_DEFS } from './range-field-defs';
import {
  CATALOG_ONLY_REAL_ARRAY_FIELD_IDS,
  CATALOG_ONLY_SCALAR_TEXT_FIELD_IDS,
} from '../features/media-units/lib/filter-fields-catalog-only';
import { coerceNoPhoto } from '../features/media-units/lib/sidecar-coercion';

/**
 * Shared filter field IDs used by both the map (/map) and list (/media-units) routes.
 * This is the single source of truth for which URL search params are treated as filter params.
 * Both routes, the shared hook, and sidebar carry-over logic import from here.
 */

/** Filter pill id for the "Near POI" proximity filter, shared across /media-units and /map. */
export const NEAR_POI_FILTER_ID = 'near-poi';

/** FilterPanel "Dodaj filtr" id for the boolean "bez zdjęcia" (no-photo) sidecar pill, shared across /media-units and /map. */
export const NO_PHOTO_FILTER_ID = 'no-photo';

/** FilterPanel "Dodaj filtr" id for the availability date-range sidecar pill, shared across /media-units and /map. */
export const AVAILABILITY_FILTER_ID = 'availability';

/**
 * When adding a new catalog filter: add its field id here ONLY if it has a spatial
 * meaning that should survive catalog → /map navigation. `pickSharedFilterSearch` below
 * ALSO carries over the catalog-only array/text selects, the numeric ranges, `noPhoto`,
 * and `availabilityFrom`/`availabilityTo` — every one of those is now a genuinely shared
 * dimension too (map-catalog-filter-parity), so a new field of one of those shapes needs
 * no change here. Only `importJobId` and `exclude` remain genuinely catalog-only (no
 * map-pins equivalent) — see `pickSharedFilterSearch`'s doc comment.
 */
export const SHARED_FILTER_FIELD_IDS = [
  'formatCode',
  'mediaType',
  'region',
  'county',
  'agglomeration',
  'city',
  'administrativeAreaIds',
  'supplierId',
  'supplierNumber',
  'grwId',
  'lighting',
] as const;

export type SharedFilterFieldId = (typeof SHARED_FILTER_FIELD_IDS)[number];

/** Parses a URL query numeric range bound (string or number) to a finite number, or undefined. */
function parseRangeNumber(raw: unknown): number | undefined {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  if (typeof raw === 'string' && raw !== '') {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/**
 * Picks every filter dimension that has a spatial meaning and should survive
 * catalog → /map navigation: `SHARED_FILTER_FIELD_IDS`, the catalog-only
 * array/text selects (material/multiformat/tags/address/notes/roadNumber),
 * every numeric range (area/width/height/audience + all cost dimensions),
 * `noPhoto`, and the availability date range. Only `importJobId` and
 * `exclude` are dropped — they have no /map equivalent at all. `search` is
 * ALSO dropped: /map has its own independent `search`/coordinate box
 * (`MapSearchHeader`) rather than sharing the catalog's free-text search
 * state, so carrying it over would silently repurpose that box's value.
 *
 * The catalog persists `address`/`notes`/`roadNumber` as a single-element
 * `string[]` (`validateMediaUnitsSearch`) but /map's schema declares them as
 * a plain `string` (`mapSearchSchema`) — this accepts either raw shape and
 * always emits the plain-string shape /map expects.
 */
// eslint-disable-next-line complexity -- five independent field-shape loops, each a simple presence/coercion check
export function pickSharedFilterSearch(
  rawSearch: Record<string, unknown>,
): Record<string, unknown> {
  const picked: Record<string, unknown> = {};
  for (const key of SHARED_FILTER_FIELD_IDS) {
    const val = rawSearch[key];
    if (Array.isArray(val) && val.every((v) => typeof v === 'string') && val.length > 0) {
      picked[key] = val;
    }
  }
  for (const key of CATALOG_ONLY_REAL_ARRAY_FIELD_IDS) {
    const val = rawSearch[key];
    if (Array.isArray(val) && val.every((v) => typeof v === 'string') && val.length > 0) {
      picked[key] = val;
    }
  }
  for (const key of CATALOG_ONLY_SCALAR_TEXT_FIELD_IDS) {
    const val = rawSearch[key];
    const scalar: unknown = Array.isArray(val) ? val[0] : val;
    if (typeof scalar === 'string' && scalar) {
      picked[key] = scalar;
    }
  }
  for (const def of RANGE_FIELD_DEFS) {
    const min = parseRangeNumber(rawSearch[def.minKey]);
    if (min !== undefined) picked[def.minKey] = min;
    const max = parseRangeNumber(rawSearch[def.maxKey]);
    if (max !== undefined) picked[def.maxKey] = max;
  }
  if (coerceNoPhoto(rawSearch['noPhoto'])) picked['noPhoto'] = true;
  if (typeof rawSearch['availabilityFrom'] === 'string' && rawSearch['availabilityFrom']) {
    picked['availabilityFrom'] = rawSearch['availabilityFrom'];
  }
  if (typeof rawSearch['availabilityTo'] === 'string' && rawSearch['availabilityTo']) {
    picked['availabilityTo'] = rawSearch['availabilityTo'];
  }
  return picked;
}
